import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import mentionStyles from '../mentions.module.scss'
import { ActionCommandAutocompleteItem } from './actionCommandAutocompleteSource'
import { keepActionCommandItemVisible } from './actionCommandSuggestionScroll'

interface ActionCommandSuggestionListProps {
    items: ActionCommandAutocompleteItem[]
    command: (item: ActionCommandAutocompleteItem & { focusPromptOnInsert?: boolean }) => void
}

const ActionCommandSuggestionList = forwardRef<any, ActionCommandSuggestionListProps>(
    (props, ref) => {
        const [selectedIndex, setSelectedIndex] = useState(0)
        const listRef = React.useRef<HTMLDivElement | null>(null)
        const itemRefs = React.useRef<Array<HTMLDivElement | null>>([])
        const tagColumnWidth = useMemo(() => {
            const maxTagLength = props.items.reduce((max, item) => {
                return Math.max(max, (item.tag || '').length + 1)
            }, 0)
            const clampedLength = Math.max(6, Math.min(20, maxTagLength))
            return `${clampedLength}ch`
        }, [props.items])

        const selectItem = (index: number, options: { focusPromptOnInsert?: boolean } = {}) => {
            const item = props.items[index]
            if (item) {
                props.command({
                    ...item,
                    focusPromptOnInsert: options.focusPromptOnInsert === true,
                })
            }
        }

        const upHandler = () => {
            setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
        }

        const downHandler = () => {
            setSelectedIndex((selectedIndex + 1) % props.items.length)
        }

        const enterHandler = () => {
            selectItem(selectedIndex, { focusPromptOnInsert: true })
        }

        useEffect(() => {
            setSelectedIndex(0)
        }, [props.items])

        useEffect(() => {
            keepActionCommandItemVisible(listRef.current, itemRefs.current[selectedIndex])
        }, [selectedIndex, props.items])

        useImperativeHandle(ref, () => ({
            onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (event.key === 'ArrowUp') {
                    upHandler()
                    return true
                }

                if (event.key === 'ArrowDown') {
                    downHandler()
                    return true
                }

                if (event.key === 'Enter') {
                    enterHandler()
                    return true
                }

                if (event.key === 'Tab') {
                    event.preventDefault()
                    selectItem(selectedIndex, { focusPromptOnInsert: true })
                    return true
                }

                return false
            },
        }))

        return (
            <div className={mentionStyles.mentionList} ref={listRef}>
                {props.items.length ? (
                    props.items.map((item, index) => (
                        <div
                            className={`${mentionStyles.mentionItem} ${
                                index === selectedIndex ? mentionStyles.selected : ''
                            }`}
                            key={item.id}
                            ref={el => {
                                itemRefs.current[index] = el
                            }}
                            onMouseDown={event => {
                                event.preventDefault()
                            }}
                            onClick={() => selectItem(index, { focusPromptOnInsert: true })}
                        >
                            <div
                                className={mentionStyles.modelItem}
                                data-item-type={item.type}
                                style={{
                                    ...({
                                        '--action-command-col-ch': tagColumnWidth,
                                    } as React.CSSProperties),
                                }}
                            >
                                <div className={mentionStyles.actionCommandItemLayout}>
                                    <div className={mentionStyles.actionCommandToken}>
                                        <span className={mentionStyles.modelPrefix}>!</span>
                                        <span className={mentionStyles.modelName}>{item.tag}</span>
                                    </div>
                                    <div className={mentionStyles.actionCommandTextBlock}>
                                        {item.type === 'model' &&
                                        item.humanName &&
                                        item.humanName.trim().toLowerCase() !==
                                            item.tag.toLowerCase() ? (
                                            <div className={mentionStyles.actionCommandHumanName}>
                                                {item.humanName}
                                            </div>
                                        ) : null}
                                        {item.descriptionShort ? (
                                            <div className={mentionStyles.actionCommandSubtext}>
                                                {item.descriptionShort}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className={mentionStyles.mentionItem}>No results</div>
                )}
            </div>
        )
    },
)

ActionCommandSuggestionList.displayName = 'ActionCommandSuggestionList'

export default ActionCommandSuggestionList
